import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import * as crypto from 'node:crypto';
import { assertNoProfileYet, requireUser } from '../authz/access.js';
import { Student } from '../student.js';

export interface StudentProfileInput {
  userID: string;
  firstName: string;
  lastName: string;
  school: string;
  subjects: string[] | string;
  email: string;
  language?: string;
}

/** Inserts the Student profile for an existing account. Callers decide whose account that is. */
export async function insertStudentProfile({
  userID,
  firstName,
  lastName,
  school,
  subjects,
  email,
  language = 'de',
}: StudentProfileInput) {
  const studentId = crypto.randomUUID();

  const normalizedSubjects = Array.isArray(subjects)
    ? subjects.map(subject => String(subject)).filter(subject => subject.trim().length > 0)
    : typeof subjects === 'string' && subjects.trim().length > 0
      ? [subjects.trim()]
      : [];

  const student = new Student({
    _id: studentId,
    firstName,
    lastName,
    school,
    subjects: normalizedSubjects,
    email,
    userID,
    language: typeof language === 'string' && language.trim().length > 0 ? language : 'de',
  });

  const studentCollection = await getCollection(Student);
  await studentCollection.insertOne(student.toObject());
}

export const createStudentMutation = registerMutation({
  operationName: 'createStudentMutation',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => String },
    { name: 'firstName', type: () => String },
    { name: 'lastName', type: () => String },
    { name: 'school', type: () => String },
    { name: 'subjects', type: () => [String] },
    { name: 'email', type: () => String },
    { name: 'language', type: () => String },
  ],
  resolve:
    (
      _userID: string,
      firstName: string,
      lastName: string,
      school: string,
      subjects: string[] | string,
      email: string,
      language = 'de',
    ) =>
    async () => {
      // The profile always belongs to the signed-in account — `_userID` from the client is ignored, otherwise
      // anyone could plant a Teacher/Student profile (and with it a role) on any account. One profile per
      // account: the server resolves teacher vs student from whichever exists (db/utils/exam-role.ts).
      const { userID: resolvedUserID } = await requireUser();
      await assertNoProfileYet(resolvedUserID);

      await insertStudentProfile({
        userID: resolvedUserID,
        firstName,
        lastName,
        school,
        subjects,
        email,
        language,
      });

      return true;
    },
});
